import { isBefore, subHours } from 'date-fns';

import type { PlaylistItem } from '../../types/playlist';
import { MEDIA_CONTENT_TYPE } from '../constants';

import { isContentType } from './common';

export enum EventState {
  PRE_LIVE = 'PRE_LIVE',
  LIVE_UNPUBLISHED = 'LIVE_UNPUBLISHED',
  LIVE_PUBLISHED = 'LIVE_PUBLISHED',
  INSTANT_VOD = 'INSTANT_VOD',
  VOD_PUBLIC = 'VOD_PUBLIC',
}

export const enum MediaStatus {
  LIVE = 'LIVE',
  VOD = 'VOD',
  SCHEDULED = 'SCHEDULED',
}

export const isLiveEvent = (playlistItem?: PlaylistItem) => {
  return !!playlistItem && (isContentType(playlistItem, MEDIA_CONTENT_TYPE.liveEvent) || isContentType(playlistItem, MEDIA_CONTENT_TYPE.liveProgram));
};

export const isScheduledOrLiveMedia = (playlistItem: PlaylistItem) => {
  return playlistItem.mediaStatus === MediaStatus.SCHEDULED || playlistItem.mediaStatus === MediaStatus.LIVE;
};

export const getMediaStatusFromEventState = (playlistItem: PlaylistItem) => {
  const eventState = playlistItem['VCH.EventState'];
  const { scheduledStart, scheduledEnd } = playlistItem;

  const now = new Date();
  const started = eventState === EventState.LIVE_PUBLISHED || (scheduledStart && scheduledStart < now);
  const live = started && scheduledEnd && scheduledEnd > now;

  if (live) {
    return MediaStatus.LIVE;
  }

  if (!eventState) {
    return MediaStatus.VOD;
  }

  switch (eventState) {
    case EventState.PRE_LIVE:
    case EventState.LIVE_UNPUBLISHED:
      return MediaStatus.SCHEDULED;
    case EventState.LIVE_PUBLISHED:
      return MediaStatus.LIVE;
    case EventState.INSTANT_VOD:
    case EventState.VOD_PUBLIC:
      return MediaStatus.VOD;
  }
};

export const isPlayableLiveEvent = (playlistItem: PlaylistItem) => {
  return playlistItem.mediaStatus === MediaStatus.LIVE || playlistItem.mediaStatus === MediaStatus.VOD;
};

export const isExpiredLiveEvent = (playlistItem: PlaylistItem, catchupHours = 0) => {
  return (
    playlistItem.mediaStatus === MediaStatus.VOD && !!playlistItem.scheduledStart && isBefore(playlistItem.scheduledStart, subHours(new Date(), catchupHours))
  );
};

export const isPlayable = isPlayableLiveEvent;
export const isExpired = isExpiredLiveEvent;

/**
 * Whether the given item should be played on the live player. The `channel_` mediaid convention is the final
 * fallback; JW never uses that convention.
 */
export const shouldUseLivePlayer = (item?: PlaylistItem): boolean => {
  if (!item) return false;

  if (isLiveEvent(item) || isContentType(item, MEDIA_CONTENT_TYPE.liveChannel)) return true;
  if (isScheduledOrLiveMedia(item)) return true;

  return !!item.mediaid?.startsWith('channel');
};
